<template>
    <div class="mb-5">
        <history-wrapper :id="HEALTH_VARS_SECTION" :defaultOpened="$route.query.section == HEALTH_VARS_SECTION" overflowVisible :healthVarsKey="healthVarsKey" :title="$t('healthvariables')" @opened="load()">
            <clinic-history-variable v-for="(item, propName) in basicVariables" :rawData="item" @updateRawData="updateRawData($event)" :key="propName" :name="propName" :value="item.values" :placeholder="item.placeholder" :variableName="item.variableName" class="mb-2" :editable="isEditable(propName) && !historyReadOnly" :graphEnabled="hasGraph(propName)"></clinic-history-variable>
            <base-button v-if="!historyReadOnly" class="mt-5 mb-3" gray @click="editHealthVars">{{ $t('addOrDeleteHealthvar') }}</base-button>
            <history-active-vars v-if="showUpdateActiveVars" :activeVars="activeVars" @activeVarsUpdated="load"></history-active-vars>
        </history-wrapper>
        <history-wrapper overflowVisible :title="$t('mood')" hideTitleOnOpen v-if="!$route.query.petId"
            hideInnerNeuCardClass>
            <mood-selector
                :item-color="BACKGROUND_COLOUR"
                :initial-selected-mood-code="lastMood"
                :read-only="!canRegisterToday"
                @mood-selected="onMoodSelected"
                :showGraphIcon="true"
                />
        </history-wrapper>
        <history-wrapper :title="$t('menttum.self-evaluation')"
            :hideViewBtn="!$store.getters['session/userHasCompanies'] && !$store.getters['session/userIsProfessional']"
            :accessBtnUrl="!historyReadOnly && $store.getters['session/userIsPatient'] ? evaluateHealthUrl : null"
            v-if="!$route.query.petId" sameTab>
            <self-evaluation-test-diagram :backgroundColor="WHITE_COLOUR"></self-evaluation-test-diagram>
        </history-wrapper>
        <history-wrapper :id="ADDITIONAL_VARS_SECTION"
            :defaultOpened="$route.query.section == ADDITIONAL_VARS_SECTION" :title="$t('howDoYouFeel')"
            v-if="!$route.query.petId">
            <p>{{ $t('clinic-history.in-last-7-days') }}</p>
            <clinic-history-variable v-for="(item, propName) in additionalVariables" :rawData="item" :key="propName"
                :name="propName" :value="item.values" :variableName="item.variableName" class="mb-2"
                :editable="!historyReadOnly" :graphEnabled="hasGraph(propName)" slider
                :showGraphByDefault="variableHasToShowGraphByDefault(propName)"
                :frequencyDays="7"></clinic-history-variable>
        </history-wrapper>
    </div>
</template>

<script>
import clinicHistoryMixin from '@/mixins/clinicHistory.mixin'
import HistoryWrapper from '@/components/views/clinicHistory/ClinicHistorySectionWrapper'
import ClinicHistoryVariable from '@/components/shared/ClinicHistoryVariable.vue'
import HistoryHealthyHabitsSection from '@/components/shared/HistoryHealthyHabitsSection.vue'
import HistoryActiveVars from '@/components/shared/HistoryActiveVars.vue'
import ClinicHistoryRepository from '@/repositories/ClinicHistoryRepository'
import helpers from '@/helpers'
import { HEIGHT, WEIGHT, AGE, TEMPERATURE, BODY_MASS_INDEX, HABITS, BLOOD_TYPE, MOOD_LEVEL, MENU_PATH, HEALTH_DATA, HEALTH_VARS_SECTION, BLOOD_PRESSURE, ADDITIONAL_VARS_SECTION, HEALTH_CONDITION_TODAY } from '@/constants/clinicHistory'
import SelfEvaluationTestDiagram from '@/components/charts/SelfEvaluationTestDiagram.vue'
import { WHITE_COLOUR, BACKGROUND_COLOUR } from '@/constants/colours'
import MoodSelector from '@/components/views/selfEvaluationTests/mood/MoodSelector.vue'

export default {
    components: {
        HistoryWrapper,
        ClinicHistoryVariable,
        HistoryHealthyHabitsSection,
        HistoryActiveVars,
        SelfEvaluationTestDiagram,
        MoodSelector
    },
    mixins: [clinicHistoryMixin],
    created () {
        this.load()
    },
    mounted () {
        if (this.$route.query.section) {
            setTimeout(() => {
                document.querySelector(`#${this.$route.query.section}`).scrollIntoView({
                    behavior: 'smooth'
                })
            }, 500)
        }
        if (this.$route.query.section && this.$route.query.variable) {
            setTimeout(() => {
                document.querySelector(`#${this.$route.query.variable}`).scrollIntoView({
                    behavior: 'smooth'
                })
            }, 500)
        }
        this.loadLastMood()
    },
    data () {
        return {
            defaultVars: [HEIGHT, WEIGHT, AGE, TEMPERATURE],
            basicVariables: [],
            additionalVariables: [],
            nonEditableVariables: [AGE, BODY_MASS_INDEX],
            withoutGraphVariables: [AGE, HABITS, BLOOD_TYPE, BODY_MASS_INDEX, MOOD_LEVEL],
            activeVars: [],
            healthVarsKey: null,
            MENU_PATH,
            expandSidebar: false,
            sidebarKey: 0,
            showUpdateActiveVars: false,
            sideBarTrigger: {
                healthVars: 0,
                otherHealthVars: 0,
                diseases: 0,
                chronicDiseases: 0,
                medication: 0,
                appointmentHistory: 0,
                observations: 0,
                significantEvents: 0,
                habits: 0,
                mood: 0,
                healthyHabits: 0,
                selfTest: 0,
                allergies: 0,
                medicationAllergies: 0,
                vaccines: 0,
                familyBackground: 0
            },
            HEALTH_DATA,
            HEALTH_VARS_SECTION,
            WHITE_COLOUR,
            BACKGROUND_COLOUR,
            ADDITIONAL_VARS_SECTION,
            lastMood: null,
            lastRegisterDateMs: null
        }
    },
    methods: {
        load () {
            this.clinicHistoryRepository.getAllVariables().then((response) => {
                let data = response.data.data
                let activeVars = [...data.activeVariables, ...this.defaultVars]
                Object.keys(data.basicVariables).forEach(item => {
                    if (item === BLOOD_PRESSURE) data.basicVariables[item].placeholder = this.$t('bloodPressureReduced')
                    if (!activeVars.some(key => key === item)) delete data.basicVariables[item]
                })
                this.activeVars = activeVars
                this.basicVariables = data.basicVariables
                this.additionalVariables = data.additionalVariables
                // Do not show health condition today in health data
                delete this.additionalVariables[HEALTH_CONDITION_TODAY]
                delete this.additionalVariables[MOOD_LEVEL]
                this.showUpdateActiveVars = false
            })
        },
        isEditable (varName) {
            return !this.nonEditableVariables.includes(varName)
        },
        hasGraph (varName) {
            return !this.withoutGraphVariables.includes(varName)
        },
        updateRawData (data) {
            let basicVars = this.basicVariables[data.varName]
            basicVars.values = [data.value]
            basicVars.dateTime = new Date()
            if (data.varName === WEIGHT || data.varName === HEIGHT) {
                this.refreshBMI()
            }
            this.healthVarsKey = helpers.randomUid()
        },
        refreshBMI () {
            let height = parseFloat(this.basicVariables[HEIGHT].values[0]) / 100
            let weight = parseFloat(this.basicVariables[WEIGHT].values[0])
            if (!height || !weight) return
            let bmi = (weight / ((height * height))).toFixed(2)
            this.basicVariables[BODY_MASS_INDEX].values = [bmi]
        },
        editHealthVars () {
            this.showUpdateActiveVars = !this.showUpdateActiveVars
        },
        variableHasToShowGraphByDefault (variableName) {
            return this.$route.query.variable === variableName && !!this.$route.query.graph
        },
        onMoodSelected (moodSelected) {
            if (!moodSelected) return
            this.showMoodSelectorModal = false
            this.loadLastMood()
        },
        loadLastMood () {
            ClinicHistoryRepository().getAllVariables().then(resp => {
                const moodVar = resp.data.data.additionalVariables.mood
                if (moodVar && moodVar.values && moodVar.values.length) {
                    this.lastMood = moodVar.values[2]
                    this.lastRegisterDateMs = new Date(
                        moodVar.dateTime
                    ).getTime()
                }
            })
        }
    },
    computed: {
        evaluateHealthUrl () {
            return '/patient/tests'
        },
        clinicHistoryRepository () {
            return ClinicHistoryRepository()
        },
        healthyHabitsUrl () {
            return this.$helpers.getHabitsUrl()
        },
        canRegisterToday () {
            if (!this.lastRegisterDateMs) return true
            const last = new Date(this.lastRegisterDateMs)
            const today = new Date()
            last.setHours(0, 0, 0, 0)
            today.setHours(0, 0, 0, 0)
            if (today.getTime() > last.getTime()) this.lastMood = null
            return today.getTime() > last.getTime()
        }
/*         sidebarData () {
            return {
                healthVars: {
                    title: this.$t('healthvariables'),
                    id: 'healthVars'
                },
                otherHealthVars: {
                    title: this.$t('additional-health-variables'),
                    id: 'otherHealthVars'
                },
                habits: {
                    title: this.$t('habits'),
                    id: 'habits'
                },
                mood: {
                    title: this.$t('mood'),
                    id: 'mood'
                },
                healthyHabits: {
                    title: this.$t('healthyHabits'),
                    id: 'healthyHabits'
                },
                selfTest: {
                    title: this.$t('menttum.self-evaluation'),
                    id: 'selfTest'
                }
            }
        } */
    }
}
</script>

<style style="scss" scoped></style>


// WEBPACK FOOTER //
// src/components/views/clinicHistory/ClinicHistoryHealthData.vue